import React, { useContext, useState } from "react";

import { saveAs } from "file-saver";
import { ChevronDown, Download, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/src/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/src/components/ui/dropdown-menu";

import ExportDisabledContext from "./ExportDisabledContext";

import type { JSX } from "react";

import type { ExportCsvOption } from "./charts.types";

interface SingleExportProps {
  fetchCsv: () => Promise<BlobPart>;
  fileName: string;
  options?: never;
  disabled?: boolean;
}

interface MenuExportProps {
  options: ExportCsvOption[];
  fetchCsv?: never;
  fileName?: never;
  disabled?: boolean;
}

type ExportCsvButtonProps = SingleExportProps | MenuExportProps;

function ExportCsvButton({
  fetchCsv,
  fileName,
  options,
  disabled,
}: ExportCsvButtonProps): JSX.Element {
  const { t } = useTranslation("editor-analytics");
  const isExportDisabled = useContext(ExportDisabledContext);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (
    getCsv: () => Promise<BlobPart>,
    name: string,
  ): Promise<void> => {
    try {
      setIsExporting(true);
      const csv = await getCsv();
      saveAs(new File([csv], name, { type: "text/csv" }));
      toast.success(t("charts.exportSuccess"));
    } catch (error) {
      toast.error(t("charts.exportError"));
      console.error(`Error exporting ${name}:`, error);
    } finally {
      setIsExporting(false);
    }
  };

  const isDisabled = isExporting || disabled || isExportDisabled;
  const content = (
    <React.Fragment>
      {isExporting ? (
        <Loader2 aria-hidden className="animate-spin" />
      ) : (
        <Download aria-hidden />
      )}
      {t("charts.export")}
    </React.Fragment>
  );

  if (options) {
    return (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button disabled={isDisabled} variant="outline">
            {content}
            <ChevronDown aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {options.map((option) => (
            <DropdownMenuItem
              key={option.fileName}
              onSelect={async () =>
                handleExport(option.fetchCsv, option.fileName)
              }
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Button
      disabled={isDisabled}
      onClick={async () => handleExport(fetchCsv, fileName)}
      variant="outline"
    >
      {content}
    </Button>
  );
}

export default ExportCsvButton;
